import React from 'react'

import Head from 'next/head'
import {serverSideTranslations} from 'next-i18next/serverSideTranslations'
import {SSRConfig, UserConfig} from 'next-i18next'
import {GetStaticPaths, GetStaticProps} from 'next'
import {StyleSheetManager, StylisPlugin} from 'styled-components'
import rtlPlugin from 'stylis-plugin-rtl'

import {Header, MiroHeader} from '@miro-site/ui-header'
import Footer, {IFooter} from '@miro-site/ui-page-sections/footer'
import {useLocalization} from '@miro-site/localization'

import TemplatePage from '../src/components/TemplatePage'
import type {TemplatePageProps} from '../src/components/TemplatePage'

import CategoryPage from '../src/components/CategoryPage'
import type {CategoryPageProps} from '../src/components/CategoryPage'

import MainPage from '../src/components/MainPage'
import type {MainPageProps} from '../src/components/MainPage'

import {
  toCategoryPage,
  toTemplatePage,
  toMeta,
  toCategoryGroup,
  toHomePage,
} from '../src/data/mappers'

import nextI18nextConfig from '../next-i18next.config'

import {
  DB,
  fetchCategories,
  fetchTemplates,
  fetchHomepages,
  fetchCategoryGroups,
  fetchHeader,
  fetchFooter,
} from '../src/data'

import {
  PageMeta,
  Menu,
  Entry,
  StaticPaths,
  Category,
  Template,
  Homepage,
  TranslatedEntry,
} from '../src/types'
import {trimSlashes} from '../src/utils/formatSlug'
import {CanonicalTags} from '../src/components/CanonicalTags/CanonicalTags'
import toTranslatedEntry from '../src/data/mappers/toTranslatedEntry'

export const getStaticPaths: GetStaticPaths = async () => {
  // Wipe clean the database files if it's starting a new production build
  if (process.env.IS_PRODUCTION_BUILD) {
    DB.wipe()
  }

  const database = await DB.getInstance()

  // Get values from database
  const entries = database.getCollectionData('entries')
  const categoryGroups = database.getCollectionData('categoryGroups')
  const menus = database.getCollectionData('menus')

  const requests = []

  if (!entries.length) {
    requests.push(fetchTemplates())
    requests.push(fetchCategories())
    requests.push(fetchHomepages())
  }

  if (!categoryGroups.length) {
    requests.push(fetchCategoryGroups())
  }

  if (!menus?.length) {
    requests.push(fetchHeader())
    requests.push(fetchFooter())
  }

  if (requests.length) {
    await Promise.all(requests)

    database.saveDatabase()
    database.generateTemplatesExport()
  }

  // Get static paths for ssg
  const staticPaths = database.getCollectionData<StaticPaths>('staticPaths', {
    removeMeta: true,
  })

  return {
    paths: staticPaths,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps = async ({params, preview = false}) => {
  const database = await DB.getInstance()

  const slug = `/${trimSlashes((params.slug as string[]).join('/'))}/`

  const entries = database.getCollection<Entry>('entries')
  const menus = database.getCollection<Menu>('menus')

  // Fetch data based on slug
  const entry = entries.findOne({slug})

  // Define which locale should be loaded
  let locale = nextI18nextConfig.i18n.defaultLocale

  // If first part of slug is a known locale, use it instead of the default
  if (nextI18nextConfig.i18n.locales.includes(params.slug[0])) {
    locale = params.slug[0]
  }

  // Fetch header and footer data
  const header = menus.findOne({$type: 'header', locale})
  const footer = menus.findOne({$type: 'footer', locale})

  // If no entry is found, return 404 page
  if (!entry) {
    return {
      notFound: true,
      props: {
        header,
        footer,
      },
    }
  }

  const {id, siteConfiguration} = entry

  // Get all entries from the same id (different locales)
  const translations = entries.find({id}).map(toTranslatedEntry)

  // Pages don't have `siteConfiguration`, other entries do
  const {meta} = siteConfiguration ?? entry

  // Load next.js translations
  const serverSidei18n = await serverSideTranslations(
    locale,
    ['common'],
    nextI18nextConfig as unknown as UserConfig,
  )

  // Define common props for all pages
  const commonProps = {
    ...serverSidei18n,
    slug,
    locale,
    meta: toMeta(meta),
    pageType: entry.$type,
    header,
    footer,
    translations,
    preview,
  }

  // Define pages mappers to expand the relationships
  const mappers = {
    template: toTemplatePage.bind(null, entry as Template),
    category: toCategoryPage.bind(null, entry as Category),
    homepage: toHomePage.bind(null, entry as Homepage),
  }

  // Gets the category groups
  const groups = database.getCollection('categoryGroups')

  // Map groups to expand the relationships
  const categoryGroups = groups
    .find({
      $type: 'categoryGroup',
      locale,
    })
    .map(toCategoryGroup)

  // Return all props needed for the page rendering
  return {
    props: {
      ...commonProps,
      pageData: {
        ...mappers[entry.$type](),
        categoryGroups,
      },
    },
    revalidate: 60,
  }
}

interface InnerPageProps extends SSRConfig {
  slug: string
  header: MiroHeader.IHeaderWithNavigation
  footer: IFooter
  meta: PageMeta
  pageType: 'category' | 'template' | 'homepage'
  pageData: TemplatePageProps | CategoryPageProps | MainPageProps
  preview?: boolean
  locale: string
  translations?: TranslatedEntry[]
}

const InnerPage: React.FC<InnerPageProps> = ({
  locale,
  slug,
  header,
  footer,
  meta,
  pageData,
  pageType,
  translations = [],
}) => {
  const {isRTL} = useLocalization()

  return (
    <StyleSheetManager stylisPlugins={isRTL && [rtlPlugin as unknown as StylisPlugin]}>
      <>
        <Head>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />

          <meta property="og:site_name" content="https://miro.com/" />
          <meta property="og:type" content="website" />
          <meta property="og:determiner" content="a" />

          <meta property="og:title" content={meta.ogTitle} />
          <meta property="og:description" content={meta.ogDescription} />
          <meta property="og:image" content={meta.ogImage} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content="https://miro.com/" />
          <meta name="twitter:title" content={meta.ogTitle} />
          <meta name="twitter:description" content={meta.ogDescription} />
          <meta name="twitter:image" content={meta.ogImage} />
        </Head>

        <CanonicalTags currentLocale={locale} meta={meta} slug={slug} translations={translations} />

        <Header {...header} currentPath={slug} />

        {pageType === 'template' && (
          <TemplatePage key={slug} {...(pageData as TemplatePageProps)} />
        )}
        {pageType === 'category' && (
          <CategoryPage key={slug} {...(pageData as CategoryPageProps)} />
        )}
        {pageType === 'homepage' && <MainPage key={slug} {...(pageData as MainPageProps)} />}

        <Footer {...footer} />
      </>
    </StyleSheetManager>
  )
}

export default InnerPage
